import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  addSymbolsToBoard,
  fetchBoard,
  fetchBoardList
} from '../../actions/marketboardRegistration';
import useLocalize from '../../utils/hooks/useLocalize';
import { getMaxSymbolsPerBoard } from '../../utils/marketboards';
import './AddToMarketBoard.scss';

const MAX_SYMBOLS_PER_BOARD = getMaxSymbolsPerBoard();

const toSymbolArray = (symbol) => {
  if (Array.isArray(symbol)) {
    return symbol;
  }
  return symbol ? [symbol] : [];
};

// A board counts as usable only if the *whole* selection fits. The AI Scanner
// registers several symbols at once (#730) and `addSymbolsToBoard` builds the
// full list before it writes, so a board with room for some but not all of
// them rejects the save outright rather than adding what fits — greying it out
// here is what keeps that from happening behind a live 「登録」 button.
// Coerced rather than trusted. This is an addition, so a `numSymbols` that
// arrived as the string "40" would concatenate instead of adding and read as
// wildly over capacity; absent or unparseable, it would make the sum NaN, and
// every comparison against NaN is false — which on this side of the test
// reads as "no room" and greys out a board with space to spare. The `<`
// comparison this replaced coerced numerically on its own and needed neither.
const boardSymbolCount = (candidate) => {
  const count = Number((candidate || {}).numSymbols);
  return Number.isFinite(count) ? count : 0;
};

const hasSpaceFor = (candidate, needed) =>
  boardSymbolCount(candidate) + needed <= MAX_SYMBOLS_PER_BOARD;

const findFirstWithSpace = (list, needed) =>
  (list || []).find((candidate) => hasSpaceFor(candidate, needed));

/**
 * The two-step "register a symbol to a Market Board" flow: the board-selection
 * modal (Figma 5271:12303) followed by the confirmation modal (5271:12435).
 *
 * The entry point is supplied per surface — this component only owns the
 * modals — so the AI Scanner results screen can reuse the same flow with its
 * own trigger and its own `classes` overrides.
 *
 * @param {object} props Component props.
 * @param {boolean} props.open Whether the flow is showing.
 * @param {Function} props.onClose Called when either modal is dismissed.
 * @param {string|Array<string>} props.symbol Symbol id(s) to register. A board
 *   is offered only when the whole set fits, so passing several narrows the
 *   selectable boards further than passing one. Read once per opening, so a
 *   surface may pass a live value without it shifting under the member.
 * @param {string} [props.className] Class on both dialog roots and on the
 *   board dropdown's menu, so a surface can scope its overrides to its own
 *   instance. All three are separate portals at the end of <body>.
 * @param {object} [props.classes] Per-surface class-name overrides.
 * @returns {JSX.Element|null} The active modal, or null while closed.
 */
export const AddToMarketBoardModal = ({
  open,
  onClose,
  symbol,
  className,
  classes = {}
}) => {
  const dispatch = useDispatch();
  const localize = useLocalize();
  const liveSymbols = useMemo(() => toSymbolArray(symbol), [symbol]);

  // Frozen for the life of one opening. A surface's selection can move while
  // the flow is on screen — the AI Scanner's candidate list is rebuilt by
  // background scan refreshes with no member action — and both the capacity
  // maths and the write have to use the set the member confirmed, not
  // whatever happens to be current when they tap 「登録」. Holding it steady
  // also keeps `needed` below from re-running the reset effect mid-flow and
  // throwing away a confirmation that has already been earned.
  const [openedWith, setOpenedWith] = useState(liveSymbols);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setOpenedWith(liveSymbols);
    }
  }
  const symbols = openedWith;

  // Floored at 1 so an empty selection never reads as "fits anywhere" and
  // leaves a full board selectable. A number, not an array — so as a
  // dependency of the effect below it compares by value (`Object.is`) and a
  // re-render that rebuilds the same selection re-triggers nothing.
  const needed = Math.max(symbols.length, 1);
  // Unique per instance so two mounted triggers cannot collide on the id the
  // dialog points `aria-labelledby` at.
  const headingId = useId();

  const [boards, setBoards] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [board, setBoard] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [registeredBoard, setRegisteredBoard] = useState(null);

  // Bumped on every fetch so a slow response that lands after the member has
  // already changed board (or closed the modal) is discarded instead of
  // overwriting the current selection's detail.
  const requestId = useRef(0);
  const listRequestId = useRef(0);
  // Last board list seen, so reopening the modal is not another blank round
  // trip. Cleared after a register because `numSymbols` has changed.
  const boardListCache = useRef(null);

  const loadBoard = useCallback(
    (boardId) => {
      const id = (requestId.current += 1);
      setBoard(null);
      return dispatch(fetchBoard(boardId))
        .then((detail) => {
          if (requestId.current === id) {
            setBoard(detail);
          }
        })
        .catch(() => {
          if (requestId.current === id) {
            setBoard(null);
          }
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const listId = (listRequestId.current += 1);
    setIsSaving(false);
    setRegisteredBoard(null);
    setIsLoadingBoards(true);

    const cached = boardListCache.current;
    const preselected = cached ? findFirstWithSpace(cached, needed) : null;
    setBoards(cached || []);
    // The pick is reset on every opening, not only when the cache is empty.
    // What fits depends on the size of *this* selection, so a board chosen
    // for a smaller one must never survive into an opening it no longer has
    // room for — leaving it selected would keep 「登録」 live over a save that
    // can only fail.
    setSelectedId(preselected ? preselected.Id : '');
    setBoard(null);
    if (preselected) {
      loadBoard(preselected.Id);
    }

    dispatch(fetchBoardList())
      .then((list) => {
        if (listRequestId.current !== listId) {
          return;
        }
        boardListCache.current = list;
        setBoards(list);
        setIsLoadingBoards(false);

        const preselectionHolds =
          preselected &&
          list.some(
            (candidate) =>
              candidate.Id === preselected.Id && hasSpaceFor(candidate, needed)
          );
        if (preselectionHolds) {
          return;
        }

        const firstWithSpace = findFirstWithSpace(list, needed);
        if (firstWithSpace) {
          setSelectedId(firstWithSpace.Id);
          loadBoard(firstWithSpace.Id);
        } else {
          // Unconditionally, not just when something was pre-selected: with
          // no board able to take the selection there is nothing valid to
          // leave selected, whatever this opening or the last one chose.
          setSelectedId('');
          setBoard(null);
        }
      })
      .catch(() => {
        if (listRequestId.current === listId) {
          setIsLoadingBoards(false);
        }
      });
  }, [dispatch, loadBoard, needed, open]);

  const handleBoardChange = useCallback(
    (event) => {
      setSelectedId(event.target.value);
      loadBoard(event.target.value);
    },
    [loadBoard]
  );

  const handleRegister = useCallback(() => {
    // An empty set is refused rather than written: `addSymbolsToBoard` would
    // happily PUT the board back with its symbols unchanged and resolve, so
    // the member would be shown 「新規登録されました」 for a registration that
    // added nothing. The surfaces all gate their trigger on a non-empty
    // selection, but that is their guard, not this component's.
    if (!board || isSaving || symbols.length === 0) {
      return;
    }
    // Same staleness guard as the two fetches: `listRequestId` is bumped on
    // every open, so a save the member abandoned by closing the modal cannot
    // surface its confirmation over the session they opened next.
    const saveId = listRequestId.current;
    setIsSaving(true);
    dispatch(addSymbolsToBoard(board, symbols))
      .then((saved) => {
        // Cleared regardless of which session is on screen — the board really
        // was written, so every cached `numSymbols` is now stale.
        boardListCache.current = null;
        if (listRequestId.current !== saveId) {
          return;
        }
        setRegisteredBoard({ Id: board.Id, Name: saved.Name });
        setIsSaving(false);
      })
      .catch(() => {
        if (listRequestId.current !== saveId) {
          return;
        }
        setIsSaving(false);
      });
  }, [board, dispatch, isSaving, symbols]);

  if (!open) {
    return null;
  }

  const isBoardListPending = isLoadingBoards && boards.length === 0;

  const closeButton = (
    <button
      type="button"
      className={classNames('add-to-board-close', classes.closeButton)}
      aria-label={localize('button.label.close')}
      onClick={onClose}
    >
      <CloseIcon />
    </button>
  );

  if (registeredBoard) {
    return (
      <Dialog
        open
        onClose={onClose}
        className={classNames(
          'add-to-board-dialog',
          'add-to-board-dialog--confirmation',
          className
        )}
        aria-labelledby={headingId}
        PaperProps={{
          className: classNames('add-to-board-paper', classes.paper)
        }}
      >
        <div className="add-to-board-confirmation-head">
          <div className="add-to-board-close-row">{closeButton}</div>
          <p
            id={headingId}
            className={classNames('add-to-board-message', classes.message)}
          >
            {localize('marketboard.register.success', {
              board: (
                <Link
                  key="board"
                  className="add-to-board-message-link"
                  to={`/marketboard/${registeredBoard.Id}`}
                  onClick={onClose}
                >
                  {registeredBoard.Name}
                </Link>
              )
            })}
          </p>
        </div>
        <button
          type="button"
          className={classNames('add-to-board-confirm', classes.confirmButton)}
          onClick={onClose}
        >
          {localize('button.label.close')}
        </button>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      className={classNames(
        'add-to-board-dialog',
        'add-to-board-dialog--select',
        className
      )}
      aria-labelledby={headingId}
      PaperProps={{
        className: classNames('add-to-board-paper', classes.paper)
      }}
    >
      <div className="add-to-board-header">
        <h2
          id={headingId}
          className={classNames('add-to-board-title', classes.title)}
        >
          {localize('marketboard.register.title')}
        </h2>
        {closeButton}
      </div>
      <Select
        className={classNames('add-to-board-select', classes.select)}
        value={selectedId}
        onChange={handleBoardChange}
        displayEmpty
        disabled={isBoardListPending}
        inputProps={{ 'aria-label': localize('marketboard.register.title') }}
        // The menu is a portal of its own, sibling to the dialog rather than
        // inside it, so it needs the surface class too — a surface that has
        // restacked the dialog must be able to restack this with it.
        MenuProps={{
          className: classNames('add-to-board-select-menu', className)
        }}
      >
        {isBoardListPending && (
          <MenuItem value="">{localize('message.status.loading')}</MenuItem>
        )}
        {boards.map((candidate) => (
          <MenuItem
            key={candidate.Id}
            value={candidate.Id}
            disabled={!hasSpaceFor(candidate, needed)}
          >
            {candidate.Name}
          </MenuItem>
        ))}
      </Select>
      <div className="add-to-board-actions">
        <button
          type="button"
          className={classNames('add-to-board-cancel', classes.cancelButton)}
          onClick={onClose}
        >
          {localize('button.label.cancel')}
        </button>
        <button
          type="button"
          className={classNames('add-to-board-submit', classes.submitButton)}
          disabled={!board || isSaving || symbols.length === 0}
          onClick={handleRegister}
        >
          {localize('marketboard.register.submit')}
        </button>
      </div>
    </Dialog>
  );
};

AddToMarketBoardModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  symbol: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  className: PropTypes.string,
  classes: PropTypes.object
};

export default AddToMarketBoardModal;
